import { useState, MouseEvent, ChangeEvent, useEffect } from 'react'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import EnhancedTableBodyTaskPool from '../components/EnhancedTableBodyTaskPool'
import { HeadCell } from '@common/components/Table/table.types'
import { Task } from '@common/config/api/client'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useTabOptionalComponent } from '@common/hooks/useTabOptionalComponent'
import { Button, Stack, Typography, TableBody, TableRow, TableCell, Paper } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useQCTasksManagementHandlers } from '../hooks/useQCTasksManagementHandlers'
import { SPACING } from '@common/theme/spacing'
import { ExtendedTask } from './QcTaskListTableCompletedTasks'

interface QcTaskListTableTaskPoolProps {
  subjectId?: string
}

const headCells: readonly HeadCell<ExtendedTask>[] = [
  { id: 'task_pseudo_id', disablePadding: true, label: 'Task Pseudo ID', disableSort: false },
  { id: 'sites', disablePadding: false, label: 'Site ID', disableSort: false },
  { id: 'subject_id', disablePadding: false, label: 'Subject ID', disableSort: false },
  { id: 'subject_blind_code', disablePadding: false, label: 'Subject Blind Code', disableSort: true },
  { id: 'timepoint_requirement', disablePadding: false, label: 'Timepoint', disableSort: false },
  { id: 'task_type', disablePadding: false, label: 'Task Type', disableSort: false },
  { id: 'task_state', disablePadding: false, label: 'Task Status', disableSort: false },
  { id: 'query_status', disablePadding: false, label: 'Query Status', disableSort: false },
  { id: 'create_date', disablePadding: false, label: 'Created At', disableSort: false },
  { id: 'action', disablePadding: false, label: '', disableSort: false },
]

function EmptyTaskPoolTable() {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">No tasks available in pool</Typography>
      <Typography variant="body2" color="text.secondary">
        There are currently no unassigned tasks in the task pool
      </Typography>
    </Stack>
  )
}

export default function QcTaskListTableTaskPool({ subjectId }: QcTaskListTableTaskPoolProps) {
  const [selected, setSelected] = useState<readonly (string | null | undefined)[]>([])
  const { user } = useExtendedAuth()
  const { setOptionalComponent, setIsComponentVisible, setIsComponentUsed } = useTabOptionalComponent()
  const { studyId } = useParams()

  const {
    order,
    orderBy,
    page,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
  } = useQCTasksManagementHandlers({
    category: 'task_pool',
    studyId: studyId!,
    subjectId,
  })

  useEffect(() => {
    setIsComponentVisible(false)
    setIsComponentUsed(false)
    setOptionalComponent(
      <Button variant="contained" size="medium" color="primary">
        Assign tasks to me
      </Button>,
    )
  }, [])

  if (isLoading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  const hasPoolTasks = data?.data?.items && data.data.items.length > 0

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data?.data?.items?.map((n: Task) => n.task_id)
      if (newSelected && newSelected.length > 0) {
        setSelected(newSelected)
        setIsComponentVisible(true)
      }
      return
    }
    setSelected([])
    setIsComponentVisible(false)
  }

  const handleClick = (_event: MouseEvent<unknown>, id?: string | null) => {
    const selectedIndex = selected.indexOf(id)
    let newSelected: readonly (string | null | undefined)[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }
    setIsComponentVisible(newSelected.length !== 0)
    setSelected(newSelected)
  }

  return (
    <Paper sx={{ p: SPACING.paperInnerPadding }}>
      <EnhancedTable<ExtendedTask>
        rows={data?.data?.items || []}
        headCells={headCells}
        order={order}
        orderBy={orderBy.split('.')[0] as keyof Task}
        selected={selected}
        rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
        rowsPerPage={paginationParams.limit || 0}
        page={page}
        next_cursor={data?.data?.pagination.next_cursor}
        previous_cursor={data?.data?.pagination.previous_cursor}
        onRequestSort={handleRequestSort}
        onSelectAllClick={handleSelectAllClick}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
      >
        {hasPoolTasks ? (
          <EnhancedTableBodyTaskPool
            rows={data?.data?.items || []}
            order={order}
            orderBy={orderBy}
            selected={selected}
            handleClick={handleClick}
            authedUserId={user?.profile.sub}
          />
        ) : (
          <TableBody>
            <TableRow>
              <TableCell colSpan={headCells.length}>
                <EmptyTaskPoolTable />
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </EnhancedTable>
    </Paper>
  )
}
